import {ClockHandsIcon} from '@github-ui/custom-icons/ClockHandsIcon'
import {LinkButton} from '@primer/react'
import {ReviewState, type CompletedReview} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Link} from '@github-ui/react-router'

import {ForYouItem} from '../shared/ForYouItem'
import {pullRequestsLayoutRoute} from '../../../../routes/layout-route'
import {pullRequestsChangesWithRangeRoute} from '../../../../routes/files-changed-routes'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {useLayoutQuery} from '../../../../page-data/loaders/use-layout-query'
import {useReviewData} from './use-review-data'
import {useOverviewBranchActions} from '../mergeability/shared/use-overview-branch-actions'

export function shouldShowChangesSinceLastReview(
  review: CompletedReview | null | undefined,
  pullRequestHeadSha: string,
): review is CompletedReview {
  if (!review || review.headSha === pullRequestHeadSha) return false

  return (
    review.state === ReviewState.APPROVED ||
    review.state === ReviewState.COMMENTED ||
    review.state === ReviewState.CHANGES_REQUESTED ||
    (review.state === ReviewState.DISMISSED && review.dismissedReviewState === ReviewState.APPROVED)
  )
}

export function changesSinceLastReviewPath({
  owner,
  repo,
  prNumber,
  headSha,
}: {
  owner: string
  repo: string
  prNumber: string
  headSha: string
}) {
  return pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number: prNumber,
    range: `${headSha}..HEAD`,
  })
}

export function ChangesSinceLastReview() {
  const branchActions = useOverviewBranchActions('changes-since-last-review')
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const {pullRequest} = useLayoutQuery().data
  const {data: review, queryStatus} = useReviewData()

  if (queryStatus !== 'success' || !shouldShowChangesSinceLastReview(review, pullRequest.headSha)) return null

  const isApproval =
    review.state === ReviewState.APPROVED ||
    (review.state === ReviewState.DISMISSED && review.dismissedReviewState === ReviewState.APPROVED)
  const changesPath = changesSinceLastReviewPath({
    owner,
    repo,
    prNumber: pr_number,
    headSha: review.headSha,
  })

  return (
    <ForYouItem
      itemId="changes-since-last-review"
      visual={{kind: 'icon', icon: ClockHandsIcon, tone: 'attention', glyphSize: 24}}
      title="New changes"
      subText={isApproval ? 'Review what changed since your approval.' : 'Review what changed since your last review.'}
      actions={[
        {
          id: 'view-new-changes',
          content: (
            <LinkButton
              as={Link}
              to={changesPath}
              size="small"
              onClick={() =>
                sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
                  item_name: 'changes-since-last-review',
                  action_name: 'view_new_changes',
                })
              }
            >
              View new changes
            </LinkButton>
          ),
        },
        ...branchActions,
      ]}
    />
  )
}
